import * as z from 'zod';

import { getMsgMax, getMsgMin } from './common-messages';

const GroupHookupTitle = z.string().min(3, getMsgMin('Title', 3)).max(70, getMsgMax('Title', 70));
const GroupHookupLongText = z.string().min(10, getMsgMin('Description', 10)).max(1000, getMsgMax('Description', 1000)).optional();

export const CreateGroupHookupValidationSchema = z.object({
  title: GroupHookupTitle,
  description: GroupHookupLongText,
  whoShouldJoin: GroupHookupLongText.optional(),
  whyShouldJoin: GroupHookupLongText.optional(),
  whatWillDiscuss: GroupHookupLongText.optional(),
  category: z.string(),
  startDate: z.string(),
  eventTimezone: z.string().optional(), // IANA timezone ID (e.g., "Europe/London"). If not provided, uses user's location timezone.
  duration: z.number(),
  // This feature is hidden behind a feature flag, so it's optional
  tags: z.array(z.string()).optional(),
  // Only used for "big events"
  maxParticipants: z.number().optional(),
  image: z.string().optional(),
  private: z.boolean(),
  // Admin-only: mark event as free (no payment required for FREE users)
  paidEvent: z.boolean().optional()
});

export const UpdateGroupHookupValidationSchema = z.object({
  title: GroupHookupTitle,
  description: GroupHookupLongText,
  whoShouldJoin: GroupHookupLongText.optional(),
  whyShouldJoin: GroupHookupLongText.optional(),
  whatWillDiscuss: GroupHookupLongText.optional(),
  sponsored: z.boolean().optional(),
  private: z.boolean(),
  // Admin-only: mark event as free (no payment required for FREE users)
  paidEvent: z.boolean().optional()
});

// Recurring GroupHookup validation
export const RecurrencePatternValidationSchema = z
  .object({
    frequency: z.enum(['DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY'], {
      required_error: 'Please select a recurrence frequency'
    }),
    interval: z.number().min(1, 'Interval must be at least 1').max(12, 'Interval cannot exceed 12').default(1),
    daysOfWeek: z.array(z.enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])).optional(),
    endCondition: z.enum(['END_DATE', 'AFTER_N_OCCURRENCES'], {
      required_error: 'Please select when the series should end'
    }),
    endDate: z.string().optional(), // ISO datetime string
    maxOccurrences: z.number().min(1, 'Must have at least 1 occurrence').max(180, 'Cannot exceed 180 occurrences (6 months)').optional()
  })
  .refine(
    (data) => {
      // If frequency is WEEKLY, daysOfWeek must be provided
      if (data.frequency === 'WEEKLY' && (!data.daysOfWeek || data.daysOfWeek.length === 0)) {
        return false;
      }
      return true;
    },
    {
      message: 'Please select at least one day of the week for weekly recurrence',
      path: ['daysOfWeek']
    }
  )
  .refine(
    (data) => {
      // If endCondition is END_DATE, endDate must be provided
      if (data.endCondition === 'END_DATE' && !data.endDate) {
        return false;
      }
      return true;
    },
    {
      message: 'Please select an end date',
      path: ['endDate']
    }
  )
  .refine(
    (data) => {
      // If endCondition is AFTER_N_OCCURRENCES, maxOccurrences must be provided
      if (data.endCondition === 'AFTER_N_OCCURRENCES' && !data.maxOccurrences) {
        return false;
      }
      return true;
    },
    {
      message: 'Please specify the number of occurrences',
      path: ['maxOccurrences']
    }
  );

export const CreateRecurringGroupHookupValidationSchema = z.object({
  template: CreateGroupHookupValidationSchema,
  recurrencePattern: RecurrencePatternValidationSchema
});
